import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_COLORS, ROLE_LABELS, formatPhoneNumber } from '../utils/teamMemberDisplay';

interface Employee {
  id: string;
  name: string;
  email: string;
  role: string;
  phone: string;
  active: boolean;
  created_at: string;
  sms_consent: boolean;
  record: {
    employment_status: string;
    contract_signed: boolean;
    work_email_created: boolean;
    background_check_cleared: boolean;
    drug_test_cleared: boolean;
    i9_completed: boolean;
    qb_employee_id: string | null;
    start_date: string | null;
  } | null;
  training_completed: number;
  training_total: number;
}

const STATUS_COLORS: Record<string, string> = {
  active: '#10B981',
  pending: '#F59E0B',
  inactive: '#6B7280',
  terminated: '#EF4444',
};

export default function EmployeesPage() {
  const { user } = useAuth();
  const isSpecialist = user?.role === 'specialist';
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState(isSpecialist ? 'salesman' : 'all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Invite modal state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('salesman');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const sendInvite = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) { setInviteError('Name and email are required'); return; }
    if (!inviteEmail.includes('@')) { setInviteError('Enter a valid email address'); return; }
    setInviting(true); setInviteError('');
    try {
      await api.post('/onboarding/invite', { name: inviteName.trim(), email: inviteEmail.trim().toLowerCase(), role: inviteRole });
      setInviteSuccess(`Invite sent to ${inviteName}!`);
      setInviteName(''); setInviteEmail(''); setInviteRole('salesman');
      // Refresh list
      const r = await api.get('/employees');
      setEmployees(r.data || []);
      setTimeout(() => { setShowInvite(false); setInviteSuccess(''); }, 2000);
    } catch (e: any) {
      setInviteError(e.response?.data?.error || 'Failed to send invite');
    } finally { setInviting(false); }
  };

  useEffect(() => {
    api.get('/employees')
      .then(r => setEmployees(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = employees.filter(e => {
    if (roleFilter !== 'all' && e.role !== roleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q);
    }
    return true;
  });

  const hrComplete = (e: Employee) =>
    e.record?.background_check_cleared && e.record?.drug_test_cleared && e.record?.i9_completed;

  return (
    <div style={{ padding: 24, background: '#0F1923', minHeight: '100%' }}>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 900, margin: 0 }}>{isSpecialist ? '📋 Onboarding' : '👥 Employees'}</h1>
          <p style={{ color: '#8FA8C0', marginTop: 6, marginBottom: 0 }}>{employees.filter(e => e.active).length} active · {employees.length} total</p>
        </div>
        <button onClick={() => { setShowInvite(true); setInviteError(''); setInviteSuccess(''); }} style={{
          background: '#10B981', color: '#fff', border: 'none', borderRadius: 12,
          padding: '12px 22px', fontWeight: 800, fontSize: 14, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>+ Add New Hire</button>
      </div>

      {/* ── Invite Modal ──────────────────────────────────────────── */}
      {showInvite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowInvite(false)}>
          <div style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 20, padding: 32, width: 440, maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Add New Hire</div>
            <div style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 24 }}>They'll receive an onboarding invite email with a link to complete their setup.</div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Full Name</label>
              <input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="e.g. John Smith"
                style={{ width: '100%', background: '#0F1923', border: '1px solid #2D4259', color: '#fff', padding: '12px 14px', borderRadius: 10, fontSize: 15, boxSizing: 'border-box' as const }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Personal Email</label>
              <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="e.g. john@gmail.com" type="email"
                style={{ width: '100%', background: '#0F1923', border: '1px solid #2D4259', color: '#fff', padding: '12px 14px', borderRadius: 10, fontSize: 15, boxSizing: 'border-box' as const }} />
            </div>

            {!isSpecialist && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Role</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                  style={{ width: '100%', background: '#0F1923', border: '1px solid #2D4259', color: '#fff', padding: '12px 14px', borderRadius: 10, fontSize: 15, boxSizing: 'border-box' as const }}>
                  <option value="salesman">Field Sales Representative</option>
                  <option value="estimator">Estimator</option>
                  <option value="foreman">Field Foreman</option>
                  <option value="contracting_officer">Contracting Officer</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
            )}

            {isSpecialist && (
              <div style={{ marginBottom: 20, background: '#10B98118', border: '1px solid #10B98144', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ color: '#10B981', fontSize: 13, fontWeight: 700 }}>👤 Field Sales Representative</div>
                <div style={{ color: '#8FA8C0', fontSize: 11, marginTop: 2 }}>All new hires you add will be onboarded as salesmen.</div>
              </div>
            )}

            {inviteError && <div style={{ color: '#EF4444', fontSize: 13, marginBottom: 12, background: '#EF444422', borderRadius: 8, padding: '8px 12px' }}>{inviteError}</div>}
            {inviteSuccess && <div style={{ color: '#10B981', fontSize: 13, marginBottom: 12, background: '#10B98122', borderRadius: 8, padding: '8px 12px' }}>✓ {inviteSuccess}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowInvite(false)} style={{
                flex: 1, background: '#243447', color: '#8FA8C0', border: '1px solid #2D4259',
                borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={sendInvite} disabled={inviting} style={{
                flex: 2, background: '#10B981', color: '#fff', border: 'none',
                borderRadius: 10, padding: '12px', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                opacity: inviting ? 0.6 : 1,
              }}>{inviting ? 'Sending...' : 'Send Invite →'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', value: employees.length, color: '#4A7EB5' },
          { label: 'Active', value: employees.filter(e => e.active).length, color: '#10B981' },
          { label: 'Contract Signed', value: employees.filter(e => e.record?.contract_signed).length, color: '#8B5CF6' },
          { label: 'HR Complete', value: employees.filter(hrComplete).length, color: '#F59E0B' },
          { label: 'In QB Payroll', value: employees.filter(e => e.record?.qb_employee_id).length, color: '#EC4899' },
        ].map(s => (
          <div key={s.label} style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 12, padding: 14 }}>
            <div style={{ color: s.color, fontSize: 26, fontWeight: 800 }}>{s.value}</div>
            <div style={{ color: '#8FA8C0', fontSize: 12 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search name or email..."
          style={{ flex: 1, minWidth: 200, background: '#1A2B3C', border: '1px solid #1E3A5F', color: '#fff', padding: '9px 14px', borderRadius: 10, fontSize: 14 }}
        />
        {['all', 'salesman', 'specialist', 'estimator', 'foreman', 'pm', 'contracting_officer', 'admin'].map(r => (
          <button key={r} onClick={() => setRoleFilter(r)} style={{
            padding: '8px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            background: roleFilter === r ? ((ROLE_COLORS[r] || '#4A7EB5') + '33') : '#1A2B3C',
            color: roleFilter === r ? (ROLE_COLORS[r] || '#4A7EB5') : '#8FA8C0',
            border: `1px solid ${roleFilter === r ? (ROLE_COLORS[r] || '#4A7EB5') : '#1E3A5F'}`,
            textTransform: 'capitalize',
          }}>{r === 'all' ? 'All Roles' : ROLE_LABELS[r] || r}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #1E3A5F', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1E3A5F' }}>
              {['Employee', 'Role', 'Status', 'HR', 'Contract', 'Training', 'QB Payroll', 'Start Date'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#8FA8C0' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#8FA8C0' }}>No employees found</td></tr>
            ) : filtered.map(e => {
              const roleColor = ROLE_COLORS[e.role] || '#4A7EB5';
              const empStatus = e.record?.employment_status === 'terminated' ? 'terminated' : e.active ? (e.record?.employment_status || 'active') : 'inactive';
              const trainingPct = e.training_total > 0 ? Math.round((e.training_completed / e.training_total) * 100) : 0;
              return (
                <tr key={e.id}
                  onClick={() => navigate(`/employees/${e.id}`)}
                  style={{ borderBottom: '1px solid #1E3A5F', cursor: 'pointer' }}
                  onMouseEnter={ev => (ev.currentTarget.style.background = '#243447')}
                  onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{e.name}</div>
                    <div style={{ color: '#4A7EB5', fontSize: 12 }}>{e.email}</div>
                    {e.phone && <div style={{ color: '#8FA8C0', fontSize: 12 }}>{formatPhoneNumber(e.phone)}</div>}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: roleColor + '22', color: roleColor, border: `1px solid ${roleColor}44`, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{ROLE_LABELS[e.role] || e.role}</span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ color: STATUS_COLORS[empStatus] || '#8FA8C0', fontWeight: 600, fontSize: 13, textTransform: 'capitalize' }}>
                      {empStatus === 'active' ? '● ' : '○ '}{empStatus}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {hrComplete(e)
                      ? <span style={{ color: '#10B981', fontWeight: 700, fontSize: 13 }}>✅ Complete</span>
                      : <span style={{ color: '#F59E0B', fontWeight: 700, fontSize: 13 }}>⚠️ Pending</span>
                    }
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {e.record?.contract_signed
                      ? <span style={{ color: '#10B981', fontSize: 13 }}>✅ Signed</span>
                      : <span style={{ color: '#EF4444', fontSize: 13 }}>✗ Missing</span>
                    }
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {e.training_total > 0 ? (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: trainingPct === 100 ? '#10B981' : '#F59E0B', fontSize: 12, fontWeight: 700 }}>{trainingPct}%</span>
                          <span style={{ color: '#8FA8C0', fontSize: 11 }}>{e.training_completed}/{e.training_total}</span>
                        </div>
                        <div style={{ height: 4, background: '#1E3A5F', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${trainingPct}%`, background: trainingPct === 100 ? '#10B981' : '#F59E0B', borderRadius: 2 }} />
                        </div>
                      </div>
                    ) : <span style={{ color: '#8FA8C0', fontSize: 12 }}>N/A</span>}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {e.record?.qb_employee_id
                      ? <span style={{ color: '#10B981', fontSize: 13 }}>✅ #{e.record.qb_employee_id}</span>
                      : <span style={{ color: '#EF4444', fontSize: 13 }}>✗ Not in QB</span>
                    }
                  </td>
                  <td style={{ padding: '14px 16px', color: '#8FA8C0', fontSize: 13 }}>
                    {e.record?.start_date ? new Date(e.record.start_date).toLocaleDateString() : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
